import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useUser } from '../../contexts/UserContext';

const SuperAdminDashboard = () => {
  const { user } = useUser();
  const [stats, setStats] = useState({
    totalOrganizations: 0,
    totalUsers: 0,
    totalPositions: 0,
    totalVotes: 0,
    totalProcessed: 0,
    platformEarnings: 0,
    voteServiceCharges: 0,
    packageRevenue: 0,
  });
  const [recentOrganizations, setRecentOrganizations] = useState([]);
  const [orgEarnings, setOrgEarnings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchStats();
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [orgsRes, statsRes, earningsRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/organizations`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_URL}/admin/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { data: { totalUsers: 0, totalPositions: 0, totalVotes: 0, totalRevenue: 0 } } })),
        axios.get(`${process.env.REACT_APP_API_URL}/admin/payouts/earnings`, {
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => ({ data: { data: [] } })),
      ]);

      const organizations = orgsRes.data.data || [];
      const adminStats = statsRes.data.data || {};
      const earnings = earningsRes.data.data || [];

      console.log('📊 Earnings Response:', earnings);
      console.log('📊 Earnings count:', earnings.length);

      // Calculate totals by currency
      const currencyTotals = earnings.reduce((acc, org) => {
        const currency = org.currency || 'UGX';
        if (!acc[currency]) {
          acc[currency] = { earnings: 0, paidOut: 0, outstanding: 0 };
        }
        acc[currency].earnings += (org.earnings?.totalEarned || 0);
        acc[currency].paidOut += (org.payouts?.totalPaidOut || 0);
        acc[currency].outstanding += (org.payouts?.outstanding || 0);
        return acc;
      }, {});

      console.log('💰 Currency Totals:', currencyTotals);

      setStats({
        totalOrganizations: organizations.length,
        totalUsers: adminStats.totalUsers || 0,
        totalPositions: adminStats.totalPositions || 0,
        totalVotes: adminStats.totalVotes || 0,
        totalProcessed: adminStats.totalProcessed || 0,
        platformEarnings: adminStats.platformEarnings || 0,
        voteServiceCharges: adminStats.voteServiceCharges || 0,
        packageRevenue: adminStats.packageRevenue || 0,
        currencyTotals,
      });
      
      setRecentOrganizations(organizations.slice(-5).reverse());
      setOrgEarnings(earnings.filter(org => org.payouts?.outstanding > 0).slice(0, 5));
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'UGX') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCurrencyBreakdown = (currencyTotals, field) => {
    if (!currencyTotals || Object.keys(currencyTotals).length === 0) {
      return formatAmount(0);
    }
    
    return Object.entries(currencyTotals)
      .map(([currency, totals]) => formatAmount(totals[field], currency))
      .join(' | ');
  };

  const statCards = [
    {
      title: 'Org Earnings',
      value: formatCurrencyBreakdown(stats.currencyTotals, 'earnings'),
      icon: 'bi-wallet2',
      color: '#3b82f6',
      description: 'Total org revenue',
      link: '/super-admin/payouts'
    },
    {
      title: 'Paid Out',
      value: formatCurrencyBreakdown(stats.currencyTotals, 'paidOut'),
      icon: 'bi-cash-coin',
      color: '#10b981',
      description: 'Total paid to orgs',
      link: '/super-admin/payouts'
    },
    {
      title: 'Outstanding',
      value: formatCurrencyBreakdown(stats.currencyTotals, 'outstanding'),
      icon: 'bi-exclamation-circle',
      color: '#f59e0b',
      description: 'Pending payouts',
      link: '/super-admin/payouts'
    },
    {
      title: 'Platform Earnings',
      value: formatAmount(stats.platformEarnings),
      icon: 'bi-cash-stack',
      color: '#8b5cf6',
      description: `Service: ${formatAmount(stats.voteServiceCharges)} | Packages: ${formatAmount(stats.packageRevenue)}`,
      link: '/super-admin/transactions'
    },
  ];

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Welcome Header */}
      <div className="mb-4">
        <h1 className="h3 mb-1" style={{ fontWeight: '600' }}>Welcome back, {user?.fullName}!</h1>
        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
          Monitor platform-wide activities and manage system settings
        </p>
      </div>

      {/* Stats Cards */}
      <div className="row mb-4">
        {statCards.map((card, index) => (
          <div key={index} className="col-lg-3 col-md-6 mb-4">
            <div className="card h-100" style={{ border: 'none', borderLeft: `4px solid ${card.color}` }}>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted" style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {card.title}
                    </div>
                    <div className="h3 mb-0 mt-2" style={{ fontWeight: '700', fontSize: 'clamp(1.125rem, 2.5vw, 1.75rem)', wordBreak: 'break-word', lineHeight: '1.3' }}>
                      {card.value}
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                      {card.description}
                    </div>
                  </div>
                  <div style={{ fontSize: '2.5rem', color: card.color, opacity: 0.3 }}>
                    <i className={card.icon}></i>
                  </div>
                </div>
                {card.link && (
                  <Link to={card.link} className="small mt-3 d-block" style={{ color: card.color, textDecoration: 'none', fontWeight: '500' }}>
                    View Details <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="row">
        {/* Quick Actions */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Quick Actions</h5>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <Link
                  to="/super-admin/organizations"
                  className="btn btn-outline-primary text-start"
                  style={{ padding: '0.75rem 1rem' }}
                >
                  <i className="bi bi-building me-2" style={{ color: '#667eea' }}></i>
                  Manage Organizations
                </Link>
                <Link
                  to="/super-admin/audit-logs"
                  className="btn btn-outline-primary text-start"
                  style={{ padding: '0.75rem 1rem' }}
                >
                  <i className="bi bi-file-text me-2" style={{ color: '#667eea' }}></i>
                  View Audit Logs
                </Link>
                <Link
                  to="/super-admin/settings"
                  className="btn btn-outline-primary text-start"
                  style={{ padding: '0.75rem 1rem' }}
                >
                  <i className="bi bi-gear me-2" style={{ color: '#667eea' }}></i>
                  System Settings
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Organizations with Outstanding Balance */}
        <div className="col-lg-6 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Pending Payouts</h5>
              <Link to="/super-admin/organizations" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>
                View All <i className="bi bi-arrow-right ms-1"></i>
              </Link>
            </div>
            <div className="card-body">
              {orgEarnings.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bi bi-check-circle" style={{ fontSize: '2rem', color: '#10b981' }}></i>
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.875rem' }}>All paid up!</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {orgEarnings.map((org, index) => (
                    <div key={index} className="list-group-item px-0 py-3" style={{ border: 'none', borderBottom: index < orgEarnings.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                            {org.name}
                          </div>
                          <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                            Earned: {formatAmount(org.earnings?.totalEarned || 0, org.currency)}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Paid: {formatAmount(org.payouts?.totalPaidOut || 0, org.currency)}
                          </div>
                        </div>
                        <div className="text-end">
                          <div className="badge" style={{ backgroundColor: '#fef3c7', color: '#92400e', fontSize: '0.75rem', fontWeight: '600' }}>
                            {formatAmount(org.payouts?.outstanding || 0, org.currency)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Organizations */}
      <div className="row">
        <div className="col-12 mb-4">
          <div className="card h-100">
            <div className="card-header bg-white d-flex justify-content-between align-items-center" style={{ borderBottom: '1px solid #e5e7eb' }}>
              <h5 className="mb-0" style={{ fontWeight: '600', fontSize: '1rem' }}>Recent Organizations</h5>
              <Link to="/super-admin/organizations" style={{ fontSize: '0.875rem', textDecoration: 'none' }}>
                View All <i className="bi bi-arrow-right ms-1"></i>
              </Link>
            </div>
            <div className="card-body">
              {recentOrganizations.length === 0 ? (
                <div className="text-center py-4">
                  <i className="bi bi-building" style={{ fontSize: '2rem', color: '#d1d5db' }}></i>
                  <p className="text-muted mt-2 mb-0" style={{ fontSize: '0.875rem' }}>No organizations yet</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {recentOrganizations.map((org, index) => (
                    <div key={index} className="list-group-item px-0 py-2" style={{ border: 'none', borderBottom: index < recentOrganizations.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                      <div className="d-flex align-items-center">
                        <div 
                          className="d-flex align-items-center justify-content-center me-3" 
                          style={{ 
                            width: '40px', 
                            height: '40px', 
                            borderRadius: '8px', 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                            color: 'white',
                            fontSize: '1rem',
                            fontWeight: '600'
                          }}
                        >
                          {org.name?.charAt(0).toUpperCase() || 'O'}
                        </div>
                        <div className="flex-grow-1 min-width-0">
                          <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                            {org.name}
                          </div>
                          <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                            {org.description || 'No description'}
                          </div>
                        </div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {new Date(org.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;
